import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { database } from '../firebaseConfig';
import { ref, push, set } from 'firebase/database';

// Lets anyone on the team report a bug or request a feature. Each submission is saved to the feedback list for the administrator.
const card = { background: '#ffffff', border: '1px solid #d8c9a8', borderRadius: 10, padding: 24, maxWidth: 680, width: '100%', boxSizing: 'border-box' };
const h1 = { fontSize: 24, fontWeight: 700, color: '#1a1a1a', margin: '0 0 8px' };
const muted = { fontSize: 14, color: '#5B6270', lineHeight: 1.5, margin: '0 0 16px' };
const label = { display: 'block', fontSize: 13, color: '#1a1a1a', fontWeight: 600, marginBottom: 4 };
const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 15, marginBottom: 16, background: '#ffffff', fontFamily: 'inherit' };
const btn = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '12px 20px', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 15 };
const radioRow = { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 };
const radioBox = (active) => ({ flex: '1 1 200px', padding: '12px 14px', borderRadius: 8, border: active ? '2px solid #B8962E' : '1px solid #d8c9a8', background: active ? '#fbf6e6' : '#ffffff', cursor: 'pointer', fontWeight: 600, color: '#1a1a1a', fontSize: 14 });

const TYPES = [
  { key: 'bug', label: 'Report a bug', hint: 'Something is broken, shows the wrong information, or does not work the way you expect.' },
  { key: 'feature', label: 'Request a feature', hint: 'An idea that would make Resi easier to use for you or your team.' },
];

export default function Feedback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userRole } = useAuth();
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!title.trim() || !details.trim()) {
      setError('Please add a short title and describe what happened or what you need.');
      return;
    }
    if (!user) {
      setError('You need to be signed in to send feedback.');
      return;
    }
    setSending(true);
    try {
      const entry = {
        type,
        title: title.trim(),
        details: details.trim(),
        status: 'new',
        submittedByUid: user.uid,
        submittedByName: user.displayName || user.email || '',
        submittedByEmail: user.email || '',
        submittedByRole: userRole || '',
        pageWhenSubmitted: location.pathname,
        createdAt: new Date().toISOString(),
      };
      const newRef = push(ref(database, 'feedback'));
      await set(newRef, entry);
      setSent(true);
      setTitle('');
      setDetails('');
    } catch (err) {
      console.error('Could not send feedback', err);
      setError('Could not send your feedback. Check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ padding: '24px 16px', boxSizing: 'border-box', fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={card}>
        <h1 style={h1}>Feedback</h1>
        <p style={muted}>Found a bug or have an idea for Resi? Send it here. The administrator reviews every submission.</p>

        {sent && (
          <p role="status" style={{ ...muted, background: '#f1f8ec', border: '1px solid #bcd9a6', borderRadius: 6, padding: '10px 12px', color: '#1a1a1a' }}>
            Thanks. Your feedback was sent to the administrator.{' '}
            <button type="button" onClick={() => setSent(false)} style={{ background: 'none', border: 'none', color: '#8C6D1F', fontWeight: 700, cursor: 'pointer', padding: 0 }}>Send another</button>
          </p>
        )}

        <form onSubmit={submit}>
          <span style={label}>What would you like to do?</span>
          <div style={radioRow} role="radiogroup" aria-label="Feedback type">
            {TYPES.map((t) => (
              <div key={t.key} role="radio" aria-checked={type === t.key} tabIndex={0} style={radioBox(type === t.key)}
                onClick={() => setType(t.key)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setType(t.key); }}>
                {t.label}
                <div style={{ fontWeight: 400, fontSize: 13, color: '#5B6270', marginTop: 4 }}>{t.hint}</div>
              </div>
            ))}
          </div>

          <label style={label} htmlFor="fbTitle">Short title</label>
          <input id="fbTitle" style={input} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)}
            placeholder={type === 'bug' ? 'For example: Listing dates show the wrong day' : 'For example: Add a referral tracker to Leads'} />

          <label style={label} htmlFor="fbDetails">{type === 'bug' ? 'What happened, and what did you expect?' : 'What would you like, and why would it help?'}</label>
          <textarea id="fbDetails" style={{ ...input, minHeight: 140, resize: 'vertical' }} value={details} onChange={(e) => setDetails(e.target.value)}
            placeholder={type === 'bug' ? 'Steps you took, the page you were on, and what you saw.' : 'Describe the feature and who would use it.'} />

          {error && <p role="alert" style={{ color: '#9b1c1c', fontSize: 14, margin: '0 0 12px' }}>{error}</p>}

          <button type="submit" style={btn} disabled={sending}>{sending ? 'Sending...' : 'Send feedback'}</button>
        </form>

        <p style={{ ...muted, marginTop: 16, marginBottom: 0, fontSize: 13 }}>
          <button type="button" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', color: '#8C6D1F', fontWeight: 600, cursor: 'pointer', padding: 0 }}>Back to dashboard</button>
        </p>
      </div>
    </div>
  );
}
